import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete, TextField, Avatar, Box, CircularProgress } from '@mui/material';
import api from '../api';
import { getThumbFromItem } from '../utils/photoUrl';

export default function UserSelect({ label, value, onChange, multiple = false, disabled = false }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Parse stored JSON string value back to object (like ObjectSelect does)
  const parsedValue = useMemo(() => {
    if (!value) return multiple ? [] : null;
    if (Array.isArray(value)) return value;
    if (typeof value === 'object' && value._id) return multiple ? [value] : value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (parsed && parsed._id) return multiple ? [parsed] : parsed;
      } catch { /* not JSON, ignore */ }
    }
    return multiple ? [] : null;
  }, [value, multiple]);

  // Immer die aktuell selektierten User in den Optionen halten
  useEffect(() => {
    if (!parsedValue || (Array.isArray(parsedValue) && parsedValue.length === 0)) return;
    setOptions(prev => {
      const ids = new Set(prev.map(u => u._id));
      const merged = [...prev];
      (Array.isArray(parsedValue) ? parsedValue : [parsedValue]).forEach(u => {
        if (u && u._id && !ids.has(u._id)) merged.push(u);
      });
      return merged;
    });
  }, [parsedValue]);

  const handleInputChange = async (event, inputValue, reason) => {
    if (reason === 'reset') return; // Kein fetch bei reset
    if (!inputValue || inputValue.length < 2) {
      setOptions(Array.isArray(parsedValue) ? parsedValue : parsedValue ? [parsedValue] : []);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/users/search?q=${encodeURIComponent(inputValue)}`);
      // Füge value-User zu den Optionen hinzu, falls sie fehlen
      const ids = new Set(res.data.map(u => u._id));
      const merged = [...res.data];
      (Array.isArray(parsedValue) ? parsedValue : parsedValue ? [parsedValue] : []).forEach(u => {
        if (u && u._id && !ids.has(u._id)) merged.push(u);
      });
      setOptions(merged);
    } catch {
      setOptions(Array.isArray(parsedValue) ? parsedValue : parsedValue ? [parsedValue] : []);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Autocomplete
      multiple={multiple}
      options={options}
      getOptionLabel={u => u && u.firstName ? `${u.firstName || ''} ${u.lastName || ''} (${u.username})` : ''}
      value={parsedValue}
      onChange={(_, newValue) => onChange(newValue)}
      onInputChange={handleInputChange}
      filterOptions={x => x} // Keine lokale Filterung, alles per API
      disabled={disabled}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Avatar src={getThumbFromItem(option, 64)} sx={{ width: 24, height: 24, mr: 1 }} />
          {option.firstName} {option.lastName} ({option.username})
        </Box>
      )}
      renderInput={params => (
        <TextField {...params} label={label} disabled={disabled} InputProps={{ ...params.InputProps, endAdornment: loading ? <CircularProgress size={20} /> : params.InputProps.endAdornment }} />
      )}
      isOptionEqualToValue={(opt, val) => opt._id === val._id}
    />
  );
}
